/**
 * Aadhar Masking Utility
 * Handles masking/unmasking of Aadhar numbers with OTP verification
 */

/**
 * Mask Aadhar number - show only last 4 digits
 * @param {string} aadharNumber - 12 digit Aadhar number
 * @returns {string} - Masked Aadhar (e.g., "XXXX XXXX 1234")
 */
export const maskAadhar = (aadharNumber) => {
  if (!aadharNumber) return "";
  const cleaned = aadharNumber.replace(/\s/g, "");
  if (cleaned.length !== 12) return aadharNumber;

  // Show only last 4 digits
  const masked = "XXXX XXXX " + cleaned.slice(-4);
  return masked;
};

/**
 * Format Aadhar number with spaces (XXXX XXXX XXXX)
 * @param {string} aadharNumber - 12 digit Aadhar number
 * @returns {string} - Formatted Aadhar
 */
export const formatAadhar = (aadharNumber) => {
  if (!aadharNumber) return "";
  const cleaned = aadharNumber.replace(/\s/g, "");
  if (cleaned.length !== 12) return aadharNumber;

  return cleaned.match(/.{1,4}/g).join(" ");
};

/**
 * Check if Aadhar number is masked
 * @param {string} aadharNumber - Aadhar number string
 * @returns {boolean} - True if masked
 */
export const isAadharMasked = (aadharNumber) => {
  if (!aadharNumber) return false;
  return aadharNumber.includes("X");
};

/**
 * Get Aadhar field names that should have eye icon
 * @returns {Array<string>} - Array of field names
 */
export const getAadharFieldNames = () => {
  return [
    "doc_aadhar_number",
    "doc_aadharBoth_numbers",
    "doc_aadharTrustee_number",
    "doc_directorAadhar_number",
  ];
};

/**
 * Check if a field is an Aadhar field
 * @param {string} fieldName - Field name to check
 * @returns {boolean} - True if it's an Aadhar field
 */
export const isAadharField = (fieldName) => {
  return getAadharFieldNames().includes(fieldName);
};

/**
 * Get display label for Aadhar field
 * @param {string} fieldName - Field name
 * @returns {string} - Display label
 */
export const getAadharFieldLabel = (fieldName) => {
  const labels = {
    doc_aadhar_number: "Aadhar Number",
    doc_aadharBoth_numbers: "Aadhar Numbers (Both Partners)",
    doc_aadharTrustee_number: "Trustee Aadhar Number",
    doc_directorAadhar_number: "Director Aadhar Number",
  };
  return labels[fieldName] || "Aadhar Number";
};
